/**
 * sortHelpers.ts — small, dependency-free sort utilities.
 */

/**
 * Milliseconds since epoch for a `createdAt` / `updatedAt` value that may be an
 * ISO string, a Firestore Timestamp (`{ toDate() }` or `{ seconds }`), or
 * missing. Returns 0 for absent / unparseable values (sorts them to the end on
 * a descending sort).
 *
 * Used for client-side sorting after we deliberately DROP `orderBy('createdAt')`
 * from Firestore queries — a server orderBy silently excludes any doc missing
 * the field, which made legacy imports invisible to global search. Sorting in
 * memory keeps those field-absent docs while still ordering the rest correctly,
 * and this helper tolerates both the ISO-string and Timestamp shapes.
 */
export function createdAtMs(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return isNaN(t) ? 0 : t;
  }
  const o = value as { toDate?: () => Date; seconds?: number };
  if (typeof o.toDate === 'function') {
    try {
      return o.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof o.seconds === 'number') return o.seconds * 1000;
  return 0;
}
